import * as React from 'react';
import { Avatar, HeaderFilterTable } from '@/components/common';
import { useFilters } from '@/hooks';
import { ClientFilterListData } from '@/interfaces';
import { Options } from '@/components/ui/combobox';
import { clientDirectoryService } from '@/api/clientDirectory';
import { useInfiniteQuery } from '@tanstack/react-query';
import { GET_CLIENT_DIRECTORY_CLIENT_FILTER } from '@/api';

export const ClientNameFilter = () => {
	const LIMIT_PER_PAGE = 50;
	const {
		setParamFilter,
		setParamSeveralFilters,
		hasFilter,
		removeParaFilter,
	} = useFilters();
	const observer = React.useRef<IntersectionObserver | null>(null);
	const [data, setData] = React.useState<Options[]>([]);

	const fetchData = async ({
		pageParam,
	}: {
		pageParam: number;
	}): Promise<ClientFilterListData> => {
		const response = await clientDirectoryService.getClientListFilter(
			pageParam,
			LIMIT_PER_PAGE
		);
		return response.data;
	};

	const {
		data: clientNameList,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		// isError,
		isLoading,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_CLIENT_DIRECTORY_CLIENT_FILTER],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
	});

	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | null) => {
			if (isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});
			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage]
	);

	React.useEffect(() => {
		if (clientNameList) {
			const auxList: Options[] = [];
			const listFlatMapped =
				clientNameList?.pages.flatMap((page) => page.items) || [];

			listFlatMapped.forEach((item) =>
				auxList.push({
					label: item.firstname ? `${item.firstname} ${item.lastname}` : 'N/N',
					value: item.id,
					icon: (
						<Avatar
							photoUrl={item.profile_picture || ''}
							size="small"
							firstname={item.firstname}
							lastname={item.lastname}
						/>
					),
				})
			);
			setData(auxList);
		}
	}, [clientNameList]);

	const handleSelectFilters = (value?: string) => {
		// specials filters
		switch (value) {
			case 'alphabetically':
				setParamFilter('isOrderAlph', hasFilter('isOrderAlph') ? '' : 'true');
				removeParaFilter('clientId'); // remove clientId filter
				break;
			default:
				setParamSeveralFilters([
					{ label: 'isOrderAlph', value: '' }, // reset
					{ label: 'clientId', value: value },
				]);
				break;
		}
	};

	const handleClearAll = () => {
		setParamSeveralFilters([
			{ label: 'isOrderAlph', value: '' },
			{ label: 'clientId', value: '' },
		]);
	};

	return (
		<HeaderFilterTable
			title="Client Name"
			searchTitle="Filter by name"
			isLoading={isLoading}
			filterOptions={data}
			// classNamePopoverContent="lg:!w-[343px] pb-4  pr-4"
			// classNameCommandList="!max-h-[383px] overflow-y-scroll"
			handleClearAll={handleClearAll}
			handleSelectFilters={handleSelectFilters}
			containerRef={lastItemCallback}
			hasFilter={hasFilter('isOrderAlph') || hasFilter('clientId')}
		/>
	);
};
